import { useCallback, useEffect, useState } from "react";
import { api, eur } from "@/lib/api";
import { PageHeader, KpiCard, Th, Td } from "@/components/common";
import { useI18n, intlTag } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Package, RotateCcw } from "lucide-react";
import FinanceStatement from "@/components/finance/FinanceStatement";
import FinanceExport from "@/components/finance/FinanceExport";
import CancelledShippingCosts from "@/components/finance/CancelledShippingCosts";
import DhlImport from "@/components/finance/DhlImport";

export default function Finance() {
  const { t, locale } = useI18n();
  const chName = (c) => t(`channel.${c}`, c);
  const [rep, setRep] = useState(null);
  const [channels, setChannels] = useState([]);
  const [filter, setFilter] = useState({ channel: "alle", date_from: "", date_to: "" });
  const [fyYear, setFyYear] = useState("alle");
  const nowY = new Date().getFullYear();
  const fyYears = [nowY, nowY - 1, nowY - 2, nowY - 3];
  const pickYear = (v) => {
    setFyYear(v);
    if (v === "alle") setFilter({ ...filter, date_from: "", date_to: "" });
    else setFilter({ ...filter, date_from: `${v}-01-01`, date_to: `${v}-12-31` });
  };

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filter.channel !== "alle") p.set("channel", filter.channel);
    if (filter.date_from) p.set("date_from", filter.date_from);
    if (filter.date_to) p.set("date_to", filter.date_to);
    api.get(`/finance/report?${p.toString()}`).then((r) => setRep(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { api.get("/sales-channels").then((r) => setChannels(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const tot = rep?.totals || {};
  const chartData = (rep?.by_channel || []).map((c) => ({ channel: chName(c.channel), [t("common.revenue")]: c.revenue, [t("fin.cogsLabel")]: -c.cogs, [t("common.margin")]: c.contribution_margin }));

  return (
    <div>
      <PageHeader title={t("fin.title")} subtitle={t("fin.subtitle")} />
      <div className="flex flex-wrap gap-3 items-end mb-6">
        <div><Label>{t("ord.colChannel")}</Label>
          <Select value={filter.channel} onValueChange={(v) => setFilter({ ...filter, channel: v })}>
            <SelectTrigger className="mt-1.5 w-44" data-testid="finance-channel"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="alle">{t("fin.allChannels")}</SelectItem>{channels.map((c) => <SelectItem key={c.id} value={c.name}>{chName(c.name)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>{t("fin.fiscalYear")}</Label>
          <Select value={fyYear} onValueChange={pickYear}>
            <SelectTrigger className="mt-1.5 w-40" data-testid="finance-fiscal-year"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="alle">{t("fin.allYears")}</SelectItem>{fyYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>{t("fin.from")}</Label><Input type="date" lang={intlTag(locale)} className="mt-1.5" value={filter.date_from} onChange={(e) => { setFyYear("alle"); setFilter({ ...filter, date_from: e.target.value }); }} data-testid="finance-from" /></div>
        <div><Label>{t("fin.to")}</Label><Input type="date" lang={intlTag(locale)} className="mt-1.5" value={filter.date_to} onChange={(e) => { setFyYear("alle"); setFilter({ ...filter, date_to: e.target.value }); }} data-testid="finance-to" /></div>
        <Button variant="outline" onClick={() => { setFyYear("alle"); setFilter({ channel: "alle", date_from: "", date_to: "" }); }}>{t("fin.reset")}</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="fin-revenue" index={0} label={t("common.revenueNet")} value={eur(tot.revenue)} icon={TrendingUp} tone="primary" />
        <KpiCard testId="fin-cogs" index={1} label={t("fin.cogsLabel")} value={eur(-tot.cogs)} icon={Package} />
        <KpiCard testId="fin-refund" index={2} label={t("fin.refunds")} value={eur(-tot.refund)} icon={RotateCcw} tone="danger" />
        <KpiCard testId="fin-cm" index={3} label={t("common.margin")} value={eur(rep?.contribution_margin)} icon={TrendingUp} tone="primary" />
      </div>

      <div className="rounded-md border border-border bg-card p-5 shadow-sm mb-6">
        <h3 className="font-heading text-lg font-medium mb-4">{t("fin.byChannel")}</h3>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="channel" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => eur(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
              <Legend />
              <Bar dataKey={t("common.revenue")} fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} />
              <Bar dataKey={t("fin.cogsLabel")} fill="hsl(var(--chart-2))" radius={[3, 3, 0, 0]} />
              <Bar dataKey={t("common.margin")} fill="hsl(var(--chart-3))" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">{t("fin.noDataPeriod")}</div>}
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
        <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("ord.colChannel")}</Th><Th right>{t("common.revenue")}</Th><Th right>{t("fin.cogsLabel")}</Th><Th right>{t("fin.refund")}</Th><Th right>{t("common.margin")}</Th></tr></thead>
          <tbody data-testid="finance-table">{(rep?.by_channel || []).map((c) => (
            <tr key={c.channel} className="hover:bg-muted/40"><Td>{chName(c.channel)}</Td><Td right mono>{eur(c.revenue)}</Td>
              <Td right mono>{eur(-c.cogs)}</Td><Td right mono>{eur(-c.refund)}</Td><Td right mono className="font-semibold">{eur(c.contribution_margin)}</Td></tr>))}
            {!(rep?.by_channel || []).length && <tr><Td className="text-muted-foreground">{t("fin.noData")}</Td></tr>}
          </tbody></table>
      </div>

      {rep?.own_withdrawals && (
        <div className="mt-6 rounded-md border border-border bg-card shadow-sm overflow-hidden" data-testid="finance-ownw">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-2">
            <h3 className="font-heading text-lg font-medium">{t("fin.ownWithdrawals")}</h3>
            <span className="text-xs text-muted-foreground">{t("fin.ownWithdrawalsHint")}</span>
          </div>
          <table className="w-full"><tbody>
            <tr className="border-b border-border"><Td>{t("fin.ekTotal", { count: `${rep.own_withdrawals.count} ${t("fin.withdrawals")}` })}</Td><Td right mono className="font-semibold">{eur(rep.own_withdrawals.total_ek)}</Td></tr>
            <tr className="border-b border-border"><Td>{t("fin.paidRefunded")}</Td><Td right mono className="text-primary">{eur(rep.own_withdrawals.paid_ek)}</Td></tr>
            <tr><Td>{t("status.offen")}</Td><Td right mono className="text-destructive font-semibold">{eur(rep.own_withdrawals.open_ek)}</Td></tr>
          </tbody></table>
        </div>
      )}

      {rep?.promotions && (
        <div className="mt-6 rounded-md border border-border bg-card shadow-sm overflow-hidden" data-testid="finance-promo">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-2">
            <h3 className="font-heading text-lg font-medium">{t("fin.promotions")}</h3>
            <span className="text-xs text-muted-foreground">{t("fin.promotionsHint")}</span>
          </div>
          <table className="w-full"><tbody>
            <tr className="border-b border-border"><Td>{t("fin.ekTotal", { count: `${rep.promotions.count} ${t("ord.colItems")}` })}</Td><Td right mono className="font-semibold">{eur(rep.promotions.total_ek)}</Td></tr>
            <tr><Td>{t("fin.piecesTotal")}</Td><Td right mono>{rep.promotions.qty}</Td></tr>
          </tbody></table>
        </div>
      )}

      <div className="mt-8 rounded-md border border-border bg-card p-5 shadow-sm">
        <h3 className="font-heading mb-4 text-lg font-medium">{t("finc.sectionTitle")}</h3>
        <FinanceStatement />
      </div>

      <div className="mt-8 rounded-md border border-border bg-card p-5 shadow-sm">
        <FinanceExport />
      </div>

      <div className="mt-8 rounded-md border border-border bg-card p-5 shadow-sm">
        <CancelledShippingCosts />
      </div>

      <div className="mt-8">
        <DhlImport />
      </div>
    </div>
  );
}
